import { skipToken } from '@apollo/client/react'
import { useIntersection } from '@mantine/hooks'
import { useAtomValue } from 'jotai'
import { chunk, compact, pick } from 'lodash-es'
import {
  memo,
  type PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { withQuery } from 'ufo'
import { useImmer } from 'use-immer'
import FluentBookmark20Filled from '~icons/fluent/bookmark-20-filled'
import FluentBookmarkAdd20Filled from '~icons/fluent/bookmark-add-20-filled'
import FluentChevronLeft20Filled from '~icons/fluent/chevron-left-20-filled'
import FluentChevronRight20Filled from '~icons/fluent/chevron-right-20-filled'
import FluentDatabaseSearch20Regular from '~icons/fluent/database-search-20-regular'
import FluentFlag20Regular from '~icons/fluent/flag-20-regular'
import FluentImageArrowCounterclockwise20Regular from '~icons/fluent/image-arrow-counterclockwise-20-regular'
import FluentImageArrowForward20Regular from '~icons/fluent/image-arrow-forward-20-regular'
import FluentOpen20Filled from '~icons/fluent/open-20-filled'
import FluentTextboxSettings20Regular from '~icons/fluent/textbox-settings-20-regular'
import IcBaselineFileDownload from '~icons/ic/baseline-file-download'
import IcBaselineMoreVert from '~icons/ic/baseline-more-vert'
import IcBaselinePlaylistAdd from '~icons/ic/baseline-playlist-add'
import IcBaselineRateReview from '~icons/ic/baseline-rate-review'
import IcBaselineShare from '~icons/ic/baseline-share'
import LucideMessageSquareText from '~icons/lucide/message-square-text'
import { openSignModal } from 'src/components/Account/utils'
import { dialogModerationEdit } from 'src/components/Admin/Moderation/ModerationEditDialog'
import { CardCornerDebugInfo } from 'src/components/Admin/UserContentInfo/CardCornerDebugInfo'
import { trackArtwork } from 'src/components/ArtworkDetail/common'
import BlurHash from 'src/components/Common/BlurHash'
import { ContentFittableBox } from 'src/components/Common/ContentFittableBox'
import { ExpandableContentBox } from 'src/components/Common/ExpandableContentBox'
import MediaPreview from 'src/components/Common/MediaPreview'
import Empty from 'src/components/Empty'
import {
  encodeArtworkForImport,
  encodeTaskForImport,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/importEncode'
import {
  CommentComposer,
  CommentList,
  CommentProvider,
} from 'src/components/ItemComment'
import {
  CommentsScrollContext,
  useScrollToComments,
} from 'src/components/ItemComment/commentsScroll'
import { LikeHeart } from 'src/components/LikeButton/LikeHeart'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import { UserCancelled } from 'src/components/Modal/Confirm'
import { dialogInspectionForRAC } from 'src/components/Modal/InspectDialog'
import { Preloader } from 'src/components/Preloader/Preloader'
import { dialogReport } from 'src/components/Report/ReportDialog'
import TagList from 'src/components/Tag/TagList'
import { UIButton } from 'src/components/ui/Button'
import { UILink } from 'src/components/ui/Link'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import {
  UIMenu,
  UIMenuItem,
  type UIMenuItemProps,
  UIMenuSeparatorSymbol,
  UIMenuTrigger,
} from 'src/components/ui/Menu'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { useCommentsRedesignExperiment } from 'src/experiments/experiments/2605/commentsRedesign'
import { EmailVerifyBanner } from 'src/features/User/ForceVerifyEmail/EmailVerifyBanner'
import {
  executeMarkArtwork,
  fetchGetArtworkWithTaskDetail,
  useGetArtwork,
} from 'src/services/apollo/artwork'
import { useListChatEditingScenes } from 'src/services/apollo/chatEditing'
import {
  type ArtworkBaseFragment,
  type ArtworkDetailFragment,
  type GenerationModelDetailFragment,
  GenerationModelMarkType,
  type GenerationModelVersionDetailFragment,
  MarkType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import {
  executeMarkGenerationModel,
  useGetGenerationModel,
  useGetGenerationModelDetailByVersionId,
} from 'src/services/apollo/generation-model'
import { useLike } from 'src/services/helpers/like'
import { deleteModel } from 'src/services/helpers/model'
import { useMe } from 'src/services/helpers/user'
import { localeAtom } from 'src/stores/locale'
import { cx } from 'src/utils/cx'
import { formatNum } from 'src/utils/formatNum'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import { mdUser } from 'src/utils/markdown'
import { getAspectRatio } from 'src/utils/media'
import {
  createModelUrl,
  getBaseModelTypeFromLoraVersion,
  isLora,
  isDitV1OrMMDIT26AModel,
  isModelClaimed,
  isModelMembershipRequired,
  isModelPixaiInhouse,
  isUserLora,
  isUserMultiLora,
} from 'src/utils/model'
import { formatQs } from 'src/utils/qs'
import { dynamic } from 'src/utils/ssr'
import { getLocalizedTackName } from 'src/utils/tack'
import { startEventGroup, track, useTrackPageView } from 'src/utils/tracker'
import { isValidUrl } from 'src/utils/url'
import { ensureLogIn } from 'src/utils/user'
import { ModelDetailContext } from '../context'
import {
  dialogEditModel,
  dialogEditModelVersion,
} from '../GenerationModelEdit/ModelEditDialog'
import { dialogModelRequestEdit } from '../GenerationModelEdit/ModelEditRequest'
import { dialogModelShareCard } from '../GenerationModelList/ModelShareCard'
import ModelTypeChip, {
  ModelLoraBaseTypeChips,
  ModelVersionStatusChip,
  ModelPreviewEAChip,
  TrainingTaskStatus,
} from '../ModelTypeChip'
import LoraRecommendationListSection from './LoraRecommendationListSection'
import styles from './ModelDetail.module.css'
import { dialogClaim } from './ModelDetailClaimAction'
import { ModelDetailCommentSectionRedesign } from './ModelDetailCommentSectionRedesign'
import ModelDetailImage from './ModelDetailImage'
import { ModelDetailSection } from './ModelDetailSection'
import ModelInfoSection from './ModelInfoSection'
import { ModelTrainingTasksList } from './ModelTrainingTaskList'
import ModelVersionList from './ModelVersionList'

const ClientArtworkDetailDialog = dynamic(
  () => import('src/components/ArtworkDetail/ArtworkDetailDialog'),
  { ssr: false },
)
const ClientModelDetailCommentSection = dynamic(
  () => import('./ModelDetailCommentSectionClient'),
  { ssr: false },
)
const ClientModelRelatedArtworkSection = dynamic(
  () => import('./ModelRelatedArtworks'),
  { ssr: false },
)

type ModelDetailProps = StyleProps & {
  model: GenerationModelDetailFragment
  version?: GenerationModelVersionDetailFragment
  isSinglePage?: boolean
  /** Falls back to `version` prop, then `model.latestAvailableVersion`. Dropped if `modelId` doesn't match. */
  versionId?: string
  /** Render sticky "Use this model" footer button on mobile. */
  onUse?: () => void
  /** For dialog only */
  onClose?: () => any
}

export default function ModelDetail(props: ModelDetailProps) {
  const { isSinglePage, versionId, onUse, onClose } = props

  const trackPageView = useTrackPageView()

  useEffect(() => {
    if (!isSinglePage) {
      trackPageView({ scene: 'model-detail', modal: true })
    }
    // Only need to track once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Shadow these props
  // to provide reliable context values as they may not exist in some cases
  // (E.g., rendered from a dialog)
  const {
    data: modelData = props.model,
    loading: loadingModel,
    refetch: refetchModel,
  } = useGetGenerationModel(props.model.id)

  const effectiveVersionId = versionId ?? props.version?.id
  const {
    data: fetchedVersion,
    loading: loadingVersion,
    refetch: refetchVersion,
  } = useGetGenerationModelDetailByVersionId(effectiveVersionId ?? skipToken)

  const model = modelData?.title ? modelData : props.model
  const version = fetchedVersion?.name ? fetchedVersion : props.version

  /**
   * Avoid refetching when version id doesn't exist, e.g. in model dialog (which doesn't have version from props)
   * Should be revisited after updating to v4.0.11
   * @see https://github.com/apollographql/apollo-client/issues/12996
   */
  const mutateVersion = useCallback(() => {
    if (!effectiveVersionId) return
    return refetchVersion()
  }, [effectiveVersionId, refetchVersion])

  const ctxValue = useMemo(() => {
    return {
      model,
      loadingModel,
      // Not wrapping it like mutateVersion as props.model.id should always exist
      mutateModel: refetchModel,
      version,
      loadingVersion,
      mutateVersion,
      isSinglePage,
      onUse,
      onClose,
    }
  }, [
    model,
    loadingModel,
    refetchModel,
    version,
    loadingVersion,
    mutateVersion,
    isSinglePage,
    onUse,
    onClose,
  ])

  const sectionRef = useRef<HTMLElement>(null)
  const commentRefs = useMemo(() => new Map<string, HTMLElement>(), [])
  const commentsScrollValue = useMemo(
    () => ({ sectionRef, commentRefs }),
    [sectionRef, commentRefs],
  )

  if (!model) return null

  return (
    <ModelDetailContext.Provider value={ctxValue}>
      <CommentsScrollContext.Provider value={commentsScrollValue}>
        <div
          className={cx(
            'flex flex-col gap-4 text-sm',
            isSinglePage ? 'py-4 msm:py-6 mlg:py-8' : 'p-4',
            props.className,
          )}
        >
          <HeaderSection />
          {isSinglePage && (
            <ModelVersionList model={model} currentVersion={version} />
          )}
          {isUserLora(model.type) && <ModelTrainingTasksList model={model} />}
          <div className={styles['content-layout']}>
            <CoverSection style={{ gridArea: 'cover' }} />
            <InfoSection style={{ gridArea: 'info' }} />
            <div
              className="flex flex-col gap-4 min-w-0"
              style={{ gridArea: 'description' }}
            >
              <DescriptionSection />
              <CommentSectionSwitcher />
            </div>
          </div>
          {/*
            Related artworks are a personalised recommendation surface;
            anonymous SSR cannot fill them with anything useful, so lazy-
            load via `dynamic({ ssr: false })` and a null Suspense fallback.
            See `ClientArtworkDetailBottomSections` in artwork detail for
            the matching SSR pattern.
          */}
          {isSinglePage && (
            <Suspense fallback={null}>
              <ClientModelRelatedArtworkSection className="mt-4 pt-7 px-1 -mx-1 border-t border-zinc-300 dark:border-zinc-600" />
            </Suspense>
          )}
          {onUse && (
            <div className="sticky bottom-0 -mx-4 mt-2 px-4 py-3 bg-modal-background border-t border-black/10 dark:border-white/10 z-10 mmd:hidden">
              <UIButton
                variant="filled"
                className="w-full"
                onPress={() => {
                  try {
                    onUse()
                  } finally {
                    onClose?.()
                  }
                }}
              >
                <Text i18nKey="market:model.use-this-model" />
              </UIButton>
            </div>
          )}
        </div>
      </CommentsScrollContext.Provider>
    </ModelDetailContext.Provider>
  )
}

/*
 * ==== Header =================================================================
 */

function HeaderSection(props: StyleProps) {
  const { model, version, isSinglePage } = useContext(ModelDetailContext)
  const smallScreen = useBreakpoint('mmd')
  const locale = useAtomValue(localeAtom)
  const { isTreatment } = useCommentsRedesignExperiment()
  const scrollToComments = useScrollToComments()

  if (!model) return null

  // TODO: [MODEL_PERMISSION] Temporary solution for membership exclusive models
  const isMemberOnlyModel = isModelMembershipRequired(
    model?.latestAvailableVersion?.id,
  )

  return (
    <header {...props} className={cx(styles['header-layout'], props.className)}>
      <div
        className="flex min-w-0 max-mmd:flex-wrap max-mmd:gap-y-1"
        style={{ gridArea: 'badge' }}
      >
        <ModelTypeChip model={model} className="!py-0.5" />
        <ModelLoraBaseTypeChips className={'ml-1 gap-1'} model={model} />
        <ModelPreviewEAChip model={model} className="ml-1.5" />
        {isLora(model.type) && model.category && (
          <UILink
            href={`/${locale}/market#type=ANY_LORA&category=${model.category}`}
            className={cx(
              'inline-flex items-center px-2 py-0.5  rounded-full outline outline-1 -outline-offset-1',
              // Sizes
              'ml-0.5 msm:ml-1 max-msm:text-xs',
              // Colors
              'bg-zinc-100 dark:bg-zinc-700 outline-black/5 dark:outline-white/5',
            )}
          >
            <Text i18nKey={`market:lora-categories.${model.category}.label`} />
          </UILink>
        )}
      </div>
      <div
        className="flex items-center gap-2 *:flex-none max-mmd:justify-end max-mmd:flex-wrap max-mmd:gap-y-1"
        style={{ gridArea: 'action' }}
      >
        <LikeButton />
        {isTreatment && (
          <UIButton
            size={smallScreen ? 'small' : 'large'}
            icon={<LucideMessageSquareText className="size-5 text-zinc-400" />}
            aria-label="Jump to comments"
            onPress={scrollToComments}
          >
            {formatNum(model.commentCount ?? 0)}
          </UIButton>
        )}
        <BookmarkButton />
        <MenuButton />
        {!isSinglePage && (
          <UILink
            href={createModelUrl(model, version)}
            target="_blank"
            variant="outlined"
            size={smallScreen ? 'small' : 'large'}
            icon={<FluentOpen20Filled className="size-5" />}
            className="rounded-md"
          />
        )}
      </div>
      <h1
        className="text-xl mmd:text-2xl font-semibold flex items-center gap-2 min-w-0 break-words"
        style={{ gridArea: 'title' }}
      >
        {isMemberOnlyModel ? <MembershipIconCircled /> : null}
        {model.title}
      </h1>
      {/* This chip is shown in the versions list bar when isSinglePage */}
      {!isSinglePage && (
        <ModelVersionStatusChip
          modelVersion={version ?? model.latestAvailableVersion}
          className="text-black justify-self-start"
          style={{ gridArea: 'status' }}
        />
      )}
      <ModelTagsDisplay model={model} />
    </header>
  )
}

function ModelTagsDisplay({
  model,
}: {
  model: Pick<GenerationModelDetailFragment, 'tacks'>
}) {
  const locale = useAtomValue(localeAtom)
  const tacksAsTags = useMemo(() => {
    if (!model.tacks || model.tacks.length === 0) return []
    return model.tacks.map(tack => ({
      id: tack.id,
      name: tack.codeName,
      displayName: getLocalizedTackName(
        tack.tackTerms,
        tack.defaultName,
        locale,
      ),
    }))
  }, [locale, model.tacks])

  return (
    <div
      className="flex flex-wrap items-center gap-1"
      style={{ gridArea: 'tag' }}
    >
      {tacksAsTags.length > 0 && (
        <TagList type="model" tags={tacksAsTags} className="!contents" />
      )}
    </div>
  )
}

function LikeButton() {
  const smallScreen = useBreakpoint('mmd')
  const { model } = useContext(ModelDetailContext)
  const [{ liked, count, optimistic }, toggleLiked] = useLike(
    model?.id,
    RefType.Model,
    { liked: model?.liked, likedCount: model?.likedCount },
  )

  return (
    <UIButton
      size={smallScreen ? 'small' : 'large'}
      icon={<LikeHeart liked={Boolean(liked)} className="size-5" />}
      className={cx(optimistic && 'opacity-50')}
      onPress={async () => {
        if (
          await ensureLogIn({ fromPath: 'like' }).then(
            () => true,
            () => false,
          )
        ) {
          await toggleLiked()
        }
      }}
    >
      {formatNum(count)}
    </UIButton>
  )
}

function BookmarkButton(props: StyleProps) {
  const smallScreen = useBreakpoint('mmd')
  const { model } = useContext(ModelDetailContext)
  const modelId = model?.id
  const isBookmarked = model?.bookmarked

  async function clickBookmark() {
    try {
      if (!modelId) return

      const toProceed = await ensureLogIn({ fromPath: 'bookmark' }).then(
        () => true,
        () => false,
      )
      if (!toProceed) return

      const target = !isBookmarked
      track(target ? 'bookmark_click' : 'delete_bookmark_click', {
        scene: 'model-content',
        modelId,
      })

      await executeMarkGenerationModel({
        modelId,
        target,
        type: GenerationModelMarkType.Bookmark,
      })
    } catch (err) {
      toast.error(`Failed to bookmark (${String(err)})`)
    }
  }

  return (
    <UIButton
      variant="outlined"
      size={smallScreen ? 'small' : 'large'}
      busy="auto"
      aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
      icon={
        isBookmarked ? (
          <FluentBookmark20Filled className="size-5" />
        ) : (
          <FluentBookmarkAdd20Filled className="size-5" />
        )
      }
      className={cx(
        isBookmarked && 'text-purple-600 dark:text-purple-500',
        props.className,
      )}
      style={props.style}
      onPress={clickBookmark}
    />
  )
}

const TRAINING_STATUSES = [
  TrainingTaskStatus.waiting,
  TrainingTaskStatus.running,
] as string[]

function MenuButton() {
  const { t } = useTranslation(['market'])
  const smallScreen = useBreakpoint('mmd')
  const { model, mutateModel, version, mutateVersion } =
    useContext(ModelDetailContext)
  const { me, beingAdmin } = useMe()
  const beingMyModel = me != null && model?.authorId === me.id
  const modelIsTraining = TRAINING_STATUSES.includes(
    model?.trainingTask?.status ?? '',
  )
  const currentVersion = version ?? model?.latestAvailableVersion
  const navigate = useNavigate()

  const baseModelType = getBaseModelTypeFromLoraVersion(currentVersion)
  const canDownload =
    model?.isDownloadable && !isDitV1OrMMDIT26AModel(baseModelType)
  const downloadUrl = currentVersion?.downloadUrl

  if (!model) return null

  function _ensureLogin() {
    return ensureLogIn({ fromPath: 'more-btn' }).then(
      () => true,
      () => false,
    )
  }

  const items = compact<UIMenuItemProps>(
    [
      {
        id: 'share',
        icon: <IcBaselineShare className="size-5" />,
        onAction: () => {
          track('share_btn_click', { scene: 'model' })
          dialogModelShareCard(model, t)
        },
        children: <Text i18nKey="common:share.label" />,
      },
      {
        id: 'download',
        icon: <IcBaselineFileDownload className="size-5" />,
        disabled: !canDownload,
        onAction: () => {
          track('download_model_click', { type: model.type })
          if (!me) {
            // Refetch model after auth state changed to apply downloadUrl changes
            openSignModal(
              {
                onSuccess: () => {
                  if (version) mutateVersion()
                  else mutateModel()
                },
              },
              { fromPath: 'download-model' },
            )
            return
          } else {
            if (!isValidUrl(downloadUrl))
              return toast.error(t('market:model.notice.download-url-invalid'))
            window.open(downloadUrl, '_blank')
          }
        },
        children: canDownload ? (
          <Text i18nKey="market:model.download" />
        ) : (
          <Text i18nKey="market:model.not-downloadable" />
        ),
      },
      UIMenuSeparatorSymbol,
      me &&
        !beingMyModel &&
        !isModelPixaiInhouse(model.curations) &&
        !isModelClaimed(model) && {
          id: 'request-edit',
          icon: <IcBaselineRateReview className="size-5" />,
          onAction: () => dialogModelRequestEdit(model.id),
          children: <Text i18nKey="artwork:action.request-edit" />,
        },
      UIMenuSeparatorSymbol,
      beingMyModel &&
        currentVersion &&
        currentVersion.extra?.taskId && {
          id: 'reuse-dataset',
          icon: (
            <FluentImageArrowCounterclockwise20Regular className="size-5" />
          ),
          onAction: async () => {
            track('reuse_dataset_click', {
              location: 'more-btn',
            })
            navigate(
              withQuery('/train-lora', {
                addToModelId: model.id,
                trainingTaskId: currentVersion.extra.taskId,
              }),
            )
          },
          children: <Text i18nKey="market:submit.reuse-dataset.label" />,
        },
      UIMenuSeparatorSymbol,
      (beingMyModel || beingAdmin) && [
        {
          id: 'edit-model',
          icon: <FluentTextboxSettings20Regular className="size-5" />,
          color: beingAdmin && !beingMyModel ? ('admin' as const) : undefined,
          onAction: async () => {
            track('edit_model_click')
            dialogEditModel({ model: model, mutate: mutateModel })
          },
          children: <Text i18nKey="market:model.action.edit-model" />,
        },
        currentVersion && {
          id: 'edit-version',
          color: beingAdmin && !beingMyModel ? ('admin' as const) : undefined,
          onAction: async () => {
            track('edit_version_click', {
              location: 'more-btn',
            })
            dialogEditModelVersion(
              model,
              version ?? model.latestAvailableVersion!,
              {
                mutate: mutateVersion,
              },
            )
          },
          children: (
            <Text
              i18nKey="market:model.action.edit-version"
              context={version ? undefined : 'latest'}
            />
          ),
        },
      ],
      ((beingMyModel && !modelIsTraining) || beingAdmin) && {
        id: 'delete-model',
        color: 'danger' as const,
        onAction: async () => {
          track('delete_click')
          await deleteModel(model)
        },
        children: <Text i18nKey="market:model.action.delete-model" />,
      },
      UIMenuSeparatorSymbol,
      !beingMyModel &&
        // Claiming trained lora is not allowed
        !isUserLora(model.type) && {
          id: 'claim',
          icon: <IcBaselinePlaylistAdd className="size-5" />,
          onAction: async () => {
            if (await _ensureLogin()) {
              track('claim_click')
              dialogClaim(model.id)
            }
          },
          children: <Text i18nKey="market:model.claim.claim-model" />,
        },
      me &&
        !beingMyModel && {
          id: 'report',
          icon: <FluentFlag20Regular className="size-5" />,
          onAction: async () => {
            track('report_click')
            dialogReport(RefType.Model, model.id)
          },
          children: <Text i18nKey="artwork:action.report" />,
        },
      beingAdmin && [
        UIMenuSeparatorSymbol,
        {
          id: 'inspect',
          icon: <FluentDatabaseSearch20Regular className="size-5" />,
          color: 'admin' as const,
          onAction: () => {
            dialogInspectionForRAC({ model })
          },
          children: 'Inspect',
        },
        {
          id: 'moderation',
          color: 'admin' as const,
          onAction: async () => {
            dialogModerationEdit({
              refIds: [model.id],
              refType: RefType.Model,
              values: {
                ...pick(model.flag, ['isRealistic', 'isSensitive', 'isMinors']),
              },
              mutate: () => {
                mutateModel()
                mutateVersion()
              },
            })
          },
          children: 'Adjust Model Moderation',
        },
      ],
    ].flat(),
  ).reduce((items, it, idx, arr) => {
    // Remove unnecessary separators
    if (
      !(
        it === UIMenuSeparatorSymbol &&
        (idx === 0 ||
          idx === arr.length - 1 ||
          items.at(-1) === UIMenuSeparatorSymbol)
      )
    ) {
      items.push(it)
    }
    return items
  }, [] as UIMenuItemProps[])

  if (!items.length) return null

  return (
    <UIMenuTrigger>
      <UIButton
        size={smallScreen ? 'small' : 'large'}
        aria-label="More actions"
        icon={<IcBaselineMoreVert className="size-5" />}
      />
      <UIMenu placement="bottom end" items={items}>
        {item => <UIMenuItem {...item} />}
      </UIMenu>
    </UIMenuTrigger>
  )
}

/*
 * ==== Cover ==================================================================
 */

function CoverSection(props: StyleProps) {
  const {
    model,
    mutateModel,
    version,
    loadingVersion,
    mutateVersion,
    isSinglePage,
  } = useContext(ModelDetailContext)

  // Casting type to actual fetched
  // Refer to packages/pixai-app/src/services/documents/features/generation-model/fragment.graphql, fragment GenerationModelVersionDetail
  const previewArtworks = compact(
    (version ?? model?.latestAvailableVersion)?.previewArtworks,
  ) as ArtworkBaseFragment[]
  const itemCount = /* Cover */ 1 + (previewArtworks?.length ?? 0)
  const smallScreen = useBreakpoint('mmd')
  const pageSize = smallScreen ? 1 : 2
  const maxPage = Math.ceil(itemCount / pageSize) - 1

  const scrollerRef = useRef<HTMLDivElement>(null)
  const [activeIndicators, updateActiveIndicators] = useImmer(
    () => new Set<number>(),
  )
  const page = Math.floor(Math.min(...activeIndicators) / pageSize)

  function scrollTo(nextPage: number) {
    const pageEl = scrollerRef.current?.children[nextPage] as
      | HTMLElement
      | undefined
    pageEl && scrollerRef.current!.scrollTo({ left: pageEl.offsetLeft })
  }

  const [openedArtwork, setOpenedArtwork] = useState<ArtworkDetailFragment>()

  if (!model || itemCount === 0) return null

  const classes = {
    pageButton:
      'absolute inset-y-0 my-auto grid place-content-center size-10 text-black bg-white/80 hover:bg-white backdrop-blur rounded-full ring-1 ring-black/20',
  } as const

  return (
    <>
      <section
        className={cx(
          'relative flex flex-col py-1 -mx-1 rounded-[10px]',
          props.className,
        )}
        style={props.style}
      >
        {/* Slides */}
        <div ref={scrollerRef} className={styles['cover-scroller']}>
          {chunk(
            [
              // Cover
              <div key="cover" className="relative size-full">
                <ModelDetailImage
                  model={model}
                  version={version}
                  isSinglePage={isSinglePage}
                  mutate={() => (version ? mutateVersion() : mutateModel())}
                  className={cx(loadingVersion && 'opacity-50')}
                />
                <Preloader
                  inline
                  className={cx(
                    'absolute inset-0 m-auto',
                    !loadingVersion && 'invisible',
                  )}
                />
              </div>,
              // Preview artworks
              ...(previewArtworks?.map(artwork => (
                <PreviewArtwork
                  key={artwork.id}
                  artwork={artwork}
                  onClick={setOpenedArtwork}
                />
              )) ?? []),
            ].map((node, idx) => (
              <CoverItem
                key={idx}
                scroller={scrollerRef.current}
                onEnter={() =>
                  updateActiveIndicators(inds => {
                    inds.add(idx)
                  })
                }
                onExit={() =>
                  updateActiveIndicators(inds => {
                    inds.delete(idx)
                  })
                }
              >
                {node}
              </CoverItem>
            )),
            pageSize,
          ).map((nodes, idx) => (
            <div
              key={idx}
              className="flex-none grid gap-2 size-full px-1 snap-start snap-always"
              style={{
                gridTemplateColumns: `repeat(${itemCount > 1 ? pageSize : 1}, 1fr)`,
              }}
            >
              {nodes}
            </div>
          ))}
        </div>
        {/* Pagination buttons */}
        <button
          className={cx(
            classes.pageButton,
            '-left-2',
            page === 0 && 'invisible',
          )}
          onClick={() => scrollTo(page - 1)}
        >
          <FluentChevronLeft20Filled className="size-5" />
        </button>
        <button
          className={cx(
            classes.pageButton,
            '-right-2',
            page === maxPage && 'invisible',
          )}
          onClick={() => scrollTo(page + 1)}
        >
          <FluentChevronRight20Filled className="size-5" />
        </button>
        {/* Indicators */}
        <div
          className={cx(
            'mt-1 flex justify-center gap-1',
            itemCount <= pageSize && '!hidden',
          )}
        >
          {Array.from({ length: itemCount }).map((_, idx) => (
            <div
              key={idx}
              className={cx(
                'size-3 rounded',
                activeIndicators.has(idx)
                  ? 'bg-black dark:bg-white'
                  : 'bg-black/30 dark:bg-white/50',
              )}
              onClick={() => scrollTo(Math.floor(idx / pageSize))}
            />
          ))}
        </div>
      </section>
      <Suspense fallback={null}>
        <ClientArtworkDetailDialog
          open={openedArtwork != null}
          artwork={openedArtwork}
          onClose={() => setOpenedArtwork(undefined)}
        />
      </Suspense>
    </>
  )
}

function CoverItem(
  props: PropsWithChildren & {
    scroller: HTMLDivElement | null
    onEnter?: () => void
    onExit?: () => void
  },
) {
  const { ref: elRef, entry: intersection } = useIntersection({
    root: props.scroller,
    threshold: 0.5,
  })

  useEffect(() => {
    if (intersection?.isIntersecting) {
      props.onEnter?.()
    } else {
      props.onExit?.()
    }
  }, [intersection?.isIntersecting, props])

  return <div ref={elRef}>{props.children}</div>
}

function PreviewArtwork(props: {
  artwork: string | ArtworkDetailFragment
  onClick: (artwork: ArtworkDetailFragment) => void
}) {
  const locale = useAtomValue(localeAtom)
  const {
    data: artwork = typeof props.artwork === 'string'
      ? undefined
      : props.artwork,
  } = useGetArtwork(
    typeof props.artwork === 'string' ? props.artwork : skipToken,
  )
  const [{ liked = false, count, optimistic }, toggleLiked] = useLike(
    artwork?.id,
    RefType.Artwork,
    artwork,
  )

  const { me } = useMe()
  const navigate = useNavigate()

  // Pre-load chat editing scenes for validation (cached by Apollo)
  const { data: chatEditingScenes } = useListChatEditingScenes({})

  const useAsRef = async () => {
    trackArtwork('use_as_refer_btn_click', artwork)

    if (!me) {
      openSignModal(undefined, { fromPath: 'refer' })
      return
    }

    if (!artwork) return

    try {
      const proceed = await UIDialog.confirm({
        heading: <Text i18nKey="artwork:use-as-reference-confirm.title" />,
        content: <Text i18nKey="artwork:use-as-reference-confirm.desc" />,
      })
      if (!proceed) return

      const res = await fetchGetArtworkWithTaskDetail({
        id: artwork.id,
        tackLanguage: locale,
      }).then(res => res.data?.artwork?.taskParameters)

      trackArtwork('use_as_refer', artwork)

      executeMarkArtwork({
        artworkId: artwork.id,
        type: MarkType.Inspire,
        target: true,
      })

      // Check if this is a chat editing preset scene
      // Validate against pre-loaded valid scene IDs
      const sceneId = res?.parameters?.sceneId
      const validSceneIds = chatEditingScenes?.map(s => s.sceneId) ?? []
      if (sceneId && validSceneIds.includes(sceneId)) {
        window.open(`/generator/preset/${sceneId}`)
        return
      }

      // fallback to default use as reference action
      startEventGroup('generate_source')
      navigate(
        `/generator/image?${formatQs({
          importType: 'use_as_reference',
          artwork: encodeArtworkForImport(artwork),
          task: encodeTaskForImport(res),
        })}`,
      )
    } catch (err) {
      if (err === UserCancelled) return
    }
  }

  return (
    <div className="relative size-full rounded-md overflow-hidden">
      {artwork?.extra?.imageBlurHash && (
        <BlurHash
          hash={artwork.extra.imageBlurHash}
          className="absolute inset-0 w-full h-full"
        />
      )}
      <ContentFittableBox
        ratio={getAspectRatio(artwork?.media)}
        className="size-full"
      >
        <MediaPreview
          mediaId={artwork?.mediaId}
          thumbnail
          className="rounded-md"
          onClick={() => artwork && props.onClick(artwork)}
        />
        <div className="absolute bottom-0 right-0 flex gap-1 p-1">
          <UIButton
            size="small"
            disabled={optimistic}
            aria-label={liked ? 'Unlike artwork' : 'Like artwork'}
            icon={<LikeHeart liked={liked} className="size-5" />}
            className="!bg-white/80 dark:!bg-black/80 backdrop-blur"
            onPress={() => toggleLiked()}
          >
            {formatNum(count)}
          </UIButton>
          <UIMenuTrigger>
            <UIButton
              size="small"
              aria-label="Artwork preview actions"
              icon={<IcBaselineMoreVert className="size-5" />}
              className="!bg-white/80 dark:!bg-black/80 backdrop-blur"
            />
            <UIMenu placement="top end">
              <UIMenuItem
                icon={<FluentImageArrowForward20Regular className="size-5" />}
                onAction={useAsRef}
              >
                <Text i18nKey={'artwork:use-as-reference.label'} />
              </UIMenuItem>
            </UIMenu>
          </UIMenuTrigger>
        </div>
        <CardCornerDebugInfo extra={artwork?.extra}>
          {content => (
            <div className="absolute top-1.5 right-1.5 bg-black/80 rounded px-2 py-1.5">
              {content}
            </div>
          )}
        </CardCornerDebugInfo>
      </ContentFittableBox>
    </div>
  )
}

/*
 * ==== Info ===================================================================
 */

function InfoSection(props: StyleProps) {
  const { model, version, isSinglePage } = useContext(ModelDetailContext)

  if (!model) return null

  const trainBaseModelId =
    isUserMultiLora(model.type) && version
      ? version?.loraBaseModelId
      : model.baseModelVersion?.modelId

  return (
    <section {...props}>
      <ModelInfoSection
        model={model}
        isSinglePage={isSinglePage}
        version={version}
        trainBaseModelId={trainBaseModelId}
      />
      <LoraRecommendationListSection
        modelId={model.id}
        modelVersionId={version?.id}
      />
    </section>
  )
}

/*
 * ==== Description ============================================================
 */

function DescriptionSection(props: StyleProps) {
  const { model } = useContext(ModelDetailContext)

  return model?.extra?.description ? (
    <ModelDetailSection
      {...props}
      heading={<Text i18nKey="market:model.description.title" />}
    >
      <DescriptionRenderer>{model.extra.description}</DescriptionRenderer>
    </ModelDetailSection>
  ) : null
}

const DescriptionRenderer = memo(function DescriptionRenderer(props: {
  children: string
}) {
  return (
    <ExpandableContentBox className="grid grid-rows-1 grid-cols-1 max-h-[320px] data-[expanded]:max-h-fit">
      <MarkdownRenderer
        className="prose prose-sm prose-neutral dark:prose-invert max-w-full prose-h1:text-xl"
        renderer={mdUser}
      >
        {props.children}
      </MarkdownRenderer>
    </ExpandableContentBox>
  )
})

/*
 * ==== Comment ================================================================
 */

function CommentSectionSwitcher(props: StyleProps) {
  const { isTreatment } = useCommentsRedesignExperiment()
  return isTreatment ? (
    <ModelDetailCommentSectionRedesign {...props} />
  ) : (
    <CommentSection {...props} />
  )
}

function CommentSection(props: StyleProps) {
  const { model } = useContext(ModelDetailContext)
  const { me } = useMe()

  if (!model) return null

  const beingMyModel = me?.id === model.authorId

  return (
    <ModelDetailSection {...props} heading={<Text i18nKey="artwork:comment" />}>
      <Suspense fallback={null}>
        <ClientModelDetailCommentSection
          modelId={model.id}
          beingMyModel={beingMyModel}
        />
      </Suspense>
    </ModelDetailSection>
  )
}

/*
 * =============================================================================
 */

export function ModelDetailByModelId(
  props: {
    modelId: string
  } & Omit<ModelDetailProps, 'model'>,
) {
  const { modelId, ...rest } = props
  const { data: model, loading } = useGetGenerationModel(modelId || skipToken)

  if (!model && loading) return <Preloader />
  if (!model) return <Empty />
  return <ModelDetail model={model} {...rest} />
}
